import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, ChevronDown, ChevronRight, FolderKanban, FolderPlus, Inbox, Loader2, Pin, Plus, Search, X } from 'lucide-react';
import type { FolderKind, WorkspaceFolder } from '../../utils/workspaceFolders';
import { buildChildrenMap, canCreateFolderIn, getFolderPath, getWorkspaceCostsByFolder, getWorkspaceCountsByFolder } from '../../utils/folderTree';
import { describeLocation, formatCost, matchesQuery, pluralize, readLibraryPrefs, recentWorkspaces, type LibraryWorkspace } from '../../utils/workspaceLibrary';
import { FolderTree, type TreeLeaf } from './FolderTree';
import type { ProjectPanelContext } from './ProjectPanel';
import { ProjectTile, WorkspaceThumb } from './ProjectTile';

/** What the canvas panel is showing: the whole library, or one project (or group) inside it. */
export type CanvasPanelPlace = { kind: 'library' } | ProjectPanelContext;

export interface CanvasLibraryPanelProps {
    folders: WorkspaceFolder[];
    /** Other people's projects shared with this person, with their folders (each marked isShared): outlines under Shared with me */
    sharedFolders?: WorkspaceFolder[];
    workspaces: LibraryWorkspace[];
    loading?: boolean;
    openWorkspaceId: string | null;
    /** Where the open workspace lives: the project that is unfolded, and where New workspace creates */
    openContext: ProjectPanelContext;
    /** The folder a new workspace goes in when created next to the open one. */
    openTargetFolderId: string | null;
    expandedIds: Set<string>;
    onToggleExpand: (id: string) => void;
    onOpenWorkspace: (workspace: LibraryWorkspace) => void;
    /** Leaves the canvas for the full library page. */
    onOpenLibraryPage: () => void;
    /** Left out when the open workspace is view-only. */
    onCreateWorkspace?: (name: string, folderId: string | null) => void;
    /**
     * Makes a folder in a project (`parentId`), or a project (`kind` 'project', no parent):
     * folders only live in projects. Resolves to what was made, null when it failed (the caller
     * says why). Left out when the open workspace is view-only.
     */
    onCreateFolder?: (name: string, kind: FolderKind, parentId: string | null) => Promise<WorkspaceFolder | null>;
    renderWorkspaceMenu?: (workspace: LibraryWorkspace) => React.ReactNode;
}

const RECENT_COUNT = 5;
/** No projects shared with this person */
const NO_SHARED_FOLDERS: WorkspaceFolder[] = [];

interface NewFolderOffer {
    kind: FolderKind;
    title: string;
    /** Why no folder can be made here (nested too deep) */
    blocked?: string;
    onCreate: (name: string) => Promise<unknown>;
}

/**
 * "New workspace" and "New folder" (or "New project"), then a name field; they start closed
 * again in each place. A folder being made shows at once, until the library has it.
 */
const NewItemFields: React.FC<{ workspaceTitle: string; onCreateWorkspace?: (name: string) => void; folder?: NewFolderOffer }> = ({ workspaceTitle, onCreateWorkspace, folder }) => {
    const [making, setMaking] = useState<'workspace' | 'folder' | null>(null);
    const [name, setName] = useState('');
    const [creating, setCreating] = useState<string | null>(null);
    const folderWord = folder?.kind === 'project' ? 'project' : 'folder';
    const start = (what: 'workspace' | 'folder') => { setName(''); setMaking(what); };

    if (making) {
        const word = making === 'workspace' ? 'workspace' : folderWord;
        return (
            <form
                className="mt-3 px-1"
                onSubmit={(e) => {
                    e.preventDefault();
                    const trimmed = name.trim();
                    if (!trimmed) return;
                    setMaking(null);
                    if (making === 'workspace') {
                        onCreateWorkspace?.(trimmed);
                        return;
                    }
                    setCreating(trimmed);
                    void folder?.onCreate(trimmed).finally(() => setCreating(null));
                }}
            >
                <input
                    type="text"
                    autoFocus
                    className="ns-field"
                    placeholder={`${word.charAt(0).toUpperCase()}${word.slice(1)} name, then Enter`}
                    aria-label={`New ${word} name`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setMaking(null); }}
                    onBlur={() => { if (!name.trim()) setMaking(null); }}
                />
            </form>
        );
    }
    return (
        <div className="mt-3 px-1 flex flex-col gap-1">
            {creating && (
                <p className="ns-t-small flex items-center gap-2 px-2 py-1" role="status">
                    <Loader2 size={14} className="animate-spin shrink-0" aria-hidden="true" />
                    <span className="truncate">Creating “{creating}”…</span>
                </p>
            )}
            {/* One under the other: side by side they do not fit the panel on one line */}
            <div className="flex flex-col gap-1">
                {onCreateWorkspace && (
                    <button type="button" className="ns-btn ns-btn-ghost ns-btn-sm w-full" onClick={() => start('workspace')} title={workspaceTitle}>
                        <Plus size={14} aria-hidden="true" /> New workspace
                    </button>
                )}
                {folder && (
                    <button
                        type="button"
                        className="ns-btn ns-btn-ghost ns-btn-sm w-full"
                        onClick={() => start('folder')}
                        disabled={!!folder.blocked || !!creating}
                        title={folder.blocked || folder.title}
                    >
                        {folder.kind === 'project' ? <FolderKanban size={14} aria-hidden="true" /> : <FolderPlus size={14} aria-hidden="true" />}
                        {folder.kind === 'project' ? 'New project' : 'New folder'}
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * The canvas's library panel: the same sidebar as the library page, beside the canvas. A search,
 * what is pinned, the workspaces opened last, then every project as a tree that unfolds into its
 * folders and workspaces (the open workspace's project is unfolded, the workspace itself marked),
 * the workspaces outside any project, and what people shared. Opening a workspace from any row
 * switches the canvas to it; New workspace creates next to the open one.
 */
export const CanvasLibraryPanel: React.FC<CanvasLibraryPanelProps> = ({
    folders, sharedFolders = NO_SHARED_FOLDERS, workspaces, loading, openWorkspaceId, openContext, openTargetFolderId, expandedIds, onToggleExpand,
    onOpenWorkspace, onOpenLibraryPage, onCreateWorkspace, onCreateFolder, renderWorkspaceMenu,
}) => {
    const [query, setQuery] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);
    // What this person pinned on the library page (kept on this device); read again as workspaces change
    const [pinned, setPinned] = useState<string[]>(() => readLibraryPrefs().pinned);
    useEffect(() => { setQuery(''); setPinned(readLibraryPrefs().pinned); }, [openWorkspaceId]);
    // The workspaces outside any project fold away unless the open one is among them
    const [unfiledOpen, setUnfiledOpen] = useState(openContext.kind === 'unfiled');
    useEffect(() => { if (openContext.kind === 'unfiled') setUnfiledOpen(true); }, [openContext.kind, openWorkspaceId]);

    const byId = useMemo(() => new Map(workspaces.map(w => [w.id, w])), [workspaces]);
    const owned = useMemo(() => workspaces.filter(w => !w.isShared), [workspaces]);
    const shared = useMemo(() => workspaces.filter(w => w.isShared), [workspaces]);
    // What people shared as whole projects unfolds like this person's own projects; the rest is listed one by one
    const sharedFolderIds = useMemo(() => new Set(sharedFolders.map(f => f.id)), [sharedFolders]);
    const sharedInProjects = useMemo(() => shared.filter(w => !!w.sharedProjectId && !!w.folderId && sharedFolderIds.has(w.folderId)), [shared, sharedFolderIds]);
    const sharedDirect = useMemo(() => shared.filter(w => !sharedInProjects.includes(w)), [shared, sharedInProjects]);
    const sharedProjects = useMemo(() => sharedFolders.filter(f => f.kind === 'project'), [sharedFolders]);
    const sharedCounts = useMemo(() => getWorkspaceCountsByFolder(sharedFolders, sharedInProjects), [sharedFolders, sharedInProjects]);
    const sharedCosts = useMemo(() => getWorkspaceCostsByFolder(sharedFolders, sharedInProjects), [sharedFolders, sharedInProjects]);
    /** The shared project each shared folder belongs to (its root) */
    const sharedProjectOf = useMemo(() => new Map(sharedFolders.map(f => [f.id, getFolderPath(sharedFolders, f.id)[0]?.id ?? f.id])), [sharedFolders]);
    const sharedLeaves = useMemo(() => {
        const map = new Map<string | null, TreeLeaf[]>();
        for (const w of sharedInProjects) {
            if (!w.folderId) continue;
            const list = map.get(w.folderId) || [];
            list.push({ id: w.id, label: w.name, thumbnail: w.thumbnail, current: w.id === openWorkspaceId, cost: w.totalCost });
            map.set(w.folderId, list);
        }
        for (const list of map.values()) list.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
        return map;
    }, [sharedInProjects, openWorkspaceId]);
    const known = useMemo(() => new Set(folders.map(f => f.id)), [folders]);
    const counts = useMemo(() => getWorkspaceCountsByFolder(folders, owned), [folders, owned]);
    const costs = useMemo(() => getWorkspaceCostsByFolder(folders, owned), [folders, owned]);
    const costOf = (list: LibraryWorkspace[]) => list.reduce((sum, w) => sum + (w.totalCost ?? 0), 0);
    const projects = useMemo(() => (buildChildrenMap(folders).get(null) || []).filter(f => f.kind === 'project'), [folders]);
    const projectFolders = useMemo(() => folders.filter(f => f.kind === 'project' || known.has(getFolderPath(folders, f.id)[0]?.id ?? '')), [folders, known]);
    const unfiled = useMemo(() => owned.filter(w => !w.folderId || !known.has(w.folderId)).sort((a, b) => b.lastSaved - a.lastSaved), [owned, known]);
    const recent = useMemo(() => recentWorkspaces(workspaces, RECENT_COUNT), [workspaces]);
    const matches = useMemo(() => (query.trim()
        ? workspaces.filter(w => matchesQuery(w.name, query)).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
        : []), [workspaces, query]);
    const pinnedProjects = useMemo(() => projects.filter(p => pinned.includes(p.id)), [projects, pinned]);
    const pinnedFolders = useMemo(() => folders.filter(f => f.kind === 'folder' && pinned.includes(f.id)), [folders, pinned]);
    const pinnedWorkspaces = useMemo(() => workspaces.filter(w => pinned.includes(w.id)), [workspaces, pinned]);
    /** Who shared what, by the person's name: their projects (as outlines), then what they shared one by one */
    const sharedByPerson = useMemo(() => {
        const groups = new Map<string, { projects: WorkspaceFolder[]; workspaces: LibraryWorkspace[] }>();
        const groupOf = (by: string) => { const found = groups.get(by) ?? { projects: [], workspaces: [] }; groups.set(by, found); return found; };
        for (const p of sharedProjects) groupOf(p.sharedBy || 'Someone').projects.push(p);
        for (const w of sharedDirect) groupOf(w.sharedBy || w.ownerName || 'Someone').workspaces.push(w);
        return Array.from(groups, ([by, group]) => ({
            by,
            projects: [...group.projects].sort((a, b) => a.name.localeCompare(b.name)),
            workspaces: [...group.workspaces].sort((a, b) => b.lastSaved - a.lastSaved),
        })).sort((a, b) => a.by.localeCompare(b.by));
    }, [sharedProjects, sharedDirect]);

    // Every owned workspace under its folder: the tree unfolds a project into its folders, then them
    const leaves = useMemo(() => {
        const map = new Map<string | null, TreeLeaf[]>();
        for (const w of owned) {
            if (!w.folderId || !known.has(w.folderId)) continue;
            const list = map.get(w.folderId) || [];
            list.push({ id: w.id, label: w.name, thumbnail: w.thumbnail, current: w.id === openWorkspaceId, note: w.isLocal ? 'On this device' : undefined, cost: w.totalCost });
            map.set(w.folderId, list);
        }
        for (const list of map.values()) list.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
        return map;
    }, [owned, known, openWorkspaceId]);

    const open = (workspace: LibraryWorkspace) => { if (workspace.id !== openWorkspaceId) onOpenWorkspace(workspace); };

    // Where New workspace creates: next to the open workspace, else outside any project; never among what people shared
    const inSomeoneElses = openContext.kind === 'shared' || (openContext.kind === 'project' && !!openContext.project.isShared);
    const createIn: string | null | undefined = inSomeoneElses ? undefined : (openTargetFolderId && known.has(openTargetFolderId) ? openTargetFolderId : null);
    const createFolder = createIn ? folders.find(f => f.id === createIn) : undefined;
    const folderOffer: NewFolderOffer | undefined = !onCreateFolder || createIn === undefined ? undefined
        : createIn === null ? {
            kind: 'project',
            title: 'Create a project: folders and workspaces go in projects',
            onCreate: (name) => onCreateFolder(name, 'project', null),
        } : {
            kind: 'folder',
            title: `Create a folder in ${createFolder?.name ?? 'this project'}`,
            blocked: (() => { const check = canCreateFolderIn(folders, createIn); return check.ok ? undefined : check.reason; })(),
            onCreate: (name) => onCreateFolder(name, 'folder', createIn),
        };

    /** A workspace as one row: its picture, its name, where it is, what it cost */
    const workspaceRow = (w: LibraryWorkspace, { where = true, access = false }: { where?: boolean; access?: boolean } = {}) => (
        <li key={w.id}>
            <button
                type="button"
                className="ns-lib-row"
                aria-current={w.id === openWorkspaceId ? 'page' : undefined}
                onClick={() => open(w)}
                title={w.id === openWorkspaceId ? `${w.name} is open` : `Open ${w.name}`}
            >
                <WorkspaceThumb thumbnail={w.thumbnail} />
                <span className="min-w-0 flex-1 truncate">
                    {w.name}
                    {where && <span className="text-ink-faint font-normal"> · {describeLocation(folders, w)}</span>}
                </span>
                {w.totalCost && w.totalCost > 0 ? <span className="ns-t-meta text-ink-soft tabular-nums whitespace-nowrap" title="What its generations cost" data-row-cost>{formatCost(w.totalCost)}</span> : null}
                {access && w.isShared && <span className="ns-lib-count">{w.sharedPermission === 'edit' ? 'edit' : 'view'}</span>}
            </button>
        </li>
    );

    const sectionLabel = (id: string, text: string, trail?: React.ReactNode) => (
        <div className="ns-lib-side-label mt-5">
            <h3 id={id} className="ns-t-label">{text}</h3>
            {trail}
        </div>
    );

    return (
        <nav aria-label="Library" className="flex flex-col min-h-0" data-panel-place="library">
            <div className="flex items-center justify-between gap-2 pl-2 mb-2">
                <h2 className="ns-t-heading">Library</h2>
                <button
                    type="button"
                    className="ns-icon-btn ns-icon-btn-sm"
                    onClick={onOpenLibraryPage}
                    title="Open the library page"
                    aria-label="Open the library page"
                >
                    <ArrowUpRight aria-hidden="true" />
                </button>
            </div>

            <div className="relative mb-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft pointer-events-none" aria-hidden="true" />
                <input
                    ref={searchRef}
                    type="search"
                    className="ns-field ns-field-sm pl-9 pr-9 rounded-full"
                    placeholder="Find a workspace"
                    aria-label="Find a workspace"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery(''); }
                        if (e.key === 'Enter' && matches[0]) open(matches[0]);
                    }}
                />
                {/* The one clear button of a search field, as on the library page */}
                {query && (
                    <span className="absolute right-1 inset-y-0 flex items-center">
                        <button type="button" className="ns-icon-btn ns-icon-btn-xs" aria-label="Clear search" onClick={() => { setQuery(''); searchRef.current?.focus(); }}>
                            <X aria-hidden="true" />
                        </button>
                    </span>
                )}
            </div>

            {query.trim() ? (
                <section aria-label="Workspaces found" className="mt-2">
                    {matches.length ? (
                        <ul className="flex flex-col gap-0.5">{matches.map(w => workspaceRow(w))}</ul>
                    ) : (
                        <p className="ns-t-small px-2 py-1.5">No workspace is called that.</p>
                    )}
                </section>
            ) : loading && !workspaces.length ? (
                <div className="flex flex-col gap-2 px-2 mt-3" aria-hidden="true">
                    <div className="ns-skeleton h-6 w-5/6" /><div className="ns-skeleton h-6 w-2/3" /><div className="ns-skeleton h-6 w-3/4" />
                </div>
            ) : (
                <>
                    {(pinnedProjects.length > 0 || pinnedFolders.length > 0 || pinnedWorkspaces.length > 0) && (
                        <section aria-labelledby="canvas-library-pinned">
                            {sectionLabel('canvas-library-pinned', 'Pinned', <Pin className="w-3 h-3 text-ink-faint" aria-hidden="true" />)}
                            <ul className="flex flex-col gap-0.5">
                                {pinnedProjects.map(p => (
                                    <li key={p.id}>
                                        <button type="button" className="ns-lib-row" onClick={() => onToggleExpand(p.id)} title={`${expandedIds.has(p.id) ? 'Fold' : 'Unfold'} ${p.name}`}>
                                            <ProjectTile name={p.name} color={p.color} size="sm" />
                                            <span className="truncate flex-1 min-w-0 font-semibold">{p.name}</span>
                                            <span className="ns-lib-count">{counts.get(p.id) || 0}</span>
                                        </button>
                                    </li>
                                ))}
                                {pinnedFolders.map(f => (
                                    <li key={f.id}>
                                        <button type="button" className="ns-lib-row" onClick={() => onToggleExpand(f.id)} title={`${expandedIds.has(f.id) ? 'Fold' : 'Unfold'} ${f.name}`}>
                                            <FolderKanban className="w-4 h-4 text-ink-soft flex-none" aria-hidden="true" />
                                            <span className="truncate flex-1 min-w-0">{f.name} <span className="text-ink-faint font-normal">· {getFolderPath(folders, f.id)[0]?.name}</span></span>
                                            <span className="ns-lib-count">{counts.get(f.id) || 0}</span>
                                        </button>
                                    </li>
                                ))}
                                {pinnedWorkspaces.map(w => workspaceRow(w, { where: false, access: true }))}
                            </ul>
                        </section>
                    )}

                    {recent.length > 0 && (
                        <section aria-labelledby="canvas-library-recent">
                            {sectionLabel('canvas-library-recent', 'Recent')}
                            <ul className="flex flex-col gap-0.5">{recent.map(w => workspaceRow(w))}</ul>
                        </section>
                    )}

                    <section aria-labelledby="canvas-library-projects">
                        {sectionLabel('canvas-library-projects', projects.length ? `Projects · ${projects.length}` : 'Projects')}
                        {projects.length > 0 ? (
                            <FolderTree
                                variant="outline"
                                folders={projectFolders}
                                counts={counts}
                                costs={costs}
                                selectedId={null}
                                // A project or folder row only unfolds; the open workspace is the one highlight
                                onSelect={(id) => { if (id) onToggleExpand(id); }}
                                expandedIds={expandedIds}
                                onToggleExpand={onToggleExpand}
                                mode="nav"
                                leaves={leaves}
                                onSelectLeaf={(leaf) => { const w = byId.get(leaf.id); if (w) open(w); }}
                                renderLeafMenu={renderWorkspaceMenu ? (leaf) => { const w = byId.get(leaf.id); return w ? renderWorkspaceMenu(w) : null; } : undefined}
                                ariaLabel="Projects"
                            />
                        ) : (
                            <p className="ns-t-small px-2 py-1.5">No projects yet.</p>
                        )}
                        {unfiled.length > 0 && (
                            <div className="mt-1">
                                <button
                                    type="button"
                                    className="ns-lib-row ns-lib-row-outline"
                                    aria-expanded={unfiledOpen}
                                    onClick={() => setUnfiledOpen(v => !v)}
                                    title={unfiledOpen ? 'Fold the workspaces outside any project' : 'Unfold the workspaces outside any project'}
                                >
                                    <span className="ns-icon-btn ns-icon-btn-xs" aria-hidden="true">{unfiledOpen ? <ChevronDown /> : <ChevronRight />}</span>
                                    <Inbox className="w-4 h-4 text-ink-soft flex-none" aria-hidden="true" />
                                    <span className="truncate flex-1 min-w-0">Not in a project</span>
                                    {costOf(unfiled) > 0 && <span className="ns-t-meta text-ink-soft tabular-nums whitespace-nowrap" data-row-cost>{formatCost(costOf(unfiled))}</span>}
                                    <span className="ns-lib-count">{unfiled.length}</span>
                                </button>
                                {unfiledOpen && <ul className="flex flex-col gap-0.5 pl-6" aria-label="Workspaces not in a project">{unfiled.map(w => workspaceRow(w, { where: false }))}</ul>}
                            </div>
                        )}
                    </section>

                    {sharedByPerson.length > 0 && (
                        <section aria-labelledby="canvas-library-shared">
                            {sectionLabel('canvas-library-shared', 'Shared with me', <span className="ns-t-meta tabular-nums">{shared.length}</span>)}
                            <ul className="flex flex-col gap-2">
                                {sharedByPerson.map(group => (
                                    <li key={group.by}>
                                        <div className="flex items-center gap-2 pl-2 pr-1 mb-0.5 min-w-0">
                                            <span className="ns-avatar [--size:18px]" aria-hidden="true">{group.by.charAt(0).toUpperCase()}</span>
                                            <span className="ns-t-small truncate" title={group.by}>{group.by}</span>
                                        </div>
                                        {/* Their projects unfold into folders and workspaces, as this person's own do; the open workspace is the one highlight */}
                                        {group.projects.length > 0 && (() => {
                                            const projectIds = new Set(group.projects.map(p => p.id));
                                            return (
                                                <FolderTree
                                                    variant="outline"
                                                    folders={sharedFolders.filter(f => projectIds.has(sharedProjectOf.get(f.id) ?? f.id))}
                                                    counts={sharedCounts}
                                                    costs={sharedCosts}
                                                    selectedId={null}
                                                    onSelect={(id) => { if (id) onToggleExpand(id); }}
                                                    expandedIds={expandedIds}
                                                    onToggleExpand={onToggleExpand}
                                                    mode="nav"
                                                    renderBadge={(f) => (f.kind === 'project' ? <span className="ns-lib-count" data-shared-project={f.id}>{f.sharedPermission === 'edit' ? 'edit' : 'view'}</span> : null)}
                                                    leaves={sharedLeaves}
                                                    onSelectLeaf={(leaf) => { const w = byId.get(leaf.id); if (w) open(w); }}
                                                    ariaLabel={`Projects ${group.by} shared with you`}
                                                />
                                            );
                                        })()}
                                        {group.workspaces.length > 0 && <ul className="flex flex-col gap-0.5">{group.workspaces.map(w => workspaceRow(w, { where: false, access: true }))}</ul>}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {createIn !== undefined && (onCreateWorkspace || folderOffer) && (
                        <NewItemFields
                            workspaceTitle={createIn ? `Create in ${createFolder?.name ?? 'this project'}, next to the open workspace` : 'Create outside any project'}
                            onCreateWorkspace={onCreateWorkspace ? (name) => onCreateWorkspace(name, createIn) : undefined}
                            folder={folderOffer}
                        />
                    )}
                    <p className="sr-only">{pluralize(workspaces.length, 'workspace')} in your library</p>
                </>
            )}
        </nav>
    );
};
